import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Building2, Inbox, GitBranch, Store, Newspaper, Mail, MessageSquare, Settings, Activity, Users, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/properties", label: "Properties", icon: Building2 },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/referrals", label: "Referrals", icon: GitBranch },
  { to: "/admin/agencies", label: "Agencies", icon: Store },
  { to: "/admin/blog", label: "Blog", icon: Newspaper },
  { to: "/admin/emails", label: "Email Preview", icon: Mail },
  { to: "/admin/inbox", label: "Contacts & Leads", icon: MessageSquare },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/activity", label: "Activity Log", icon: Activity },
  { to: "/admin/users", label: "Admin Users", icon: Users },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="p-6"><Logo className="text-2xl" /><p className="text-xs text-muted-foreground mt-1">Admin dashboard</p></div>
      <nav className="px-3 flex-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-0.5 transition-colors ${isActive ? "bg-[#C81D35] text-white" : "text-[#111827]/70 hover:bg-muted"}`}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#111827]/70 hover:bg-muted"><ExternalLink className="w-4 h-4" /> View website</Link>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#111827]/70 hover:bg-muted" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        <div className="mt-2 px-3 py-2 text-xs text-muted-foreground truncate">{user?.name || user?.email}</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-border z-30">{SidebarContent()}</aside>

      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-border flex items-center justify-between px-4 h-14">
        <Logo className="text-xl" />
        <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu className="w-6 h-6" /></button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-white flex flex-col">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            {SidebarContent()}
          </aside>
        </div>
      )}

      <main className="lg:pl-64"><div className="p-5 sm:p-8 max-w-7xl mx-auto"><Outlet /></div></main>
    </div>
  );
}
